import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { SectionHead, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", company: "", service: "", budget: "", city: "", message: "" };

const fieldCls = "w-full bg-transparent border-0 border-b border-line py-4 text-lg text-[var(--bone)] placeholder:text-stone/70 focus:outline-none focus:border-[var(--gold)] transition-colors duration-300";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-stone">{label}</span>
    {children}
  </label>
);

const Select = ({ value, onChange, options, placeholder, testid }) => (
  <select data-testid={testid} value={value} onChange={onChange} className={`${fieldCls} appearance-none cursor-pointer`}>
    <option value="" className="bg-[var(--ink)]">{placeholder}</option>
    {options.map((o) => <option key={o} value={o} className="bg-[var(--ink)]">{o}</option>)}
  </select>
);

export const Contact = ({ defaultServiceSlug }) => {
  const { t, lang } = useLang();
  const c = t.contact;
  const allServices = t.services_data.flatMap((g) => g.items);
  const preset = allServices.find((i) => i.slug === defaultServiceSlug)?.name || "";
  const [f, setF] = useState({ ...empty, service: preset });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const serviceOpts = allServices.map((i) => i.name);
  const cityOpts = t.houses.list.map((h) => h.city);

  // Keep the pre-selected expertise in sync when the language toggles (option labels change)
  useEffect(() => { if (preset) setF((s) => ({ ...s, service: preset })); }, [preset]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) return toast.error(c.required);
    setBusy(true);
    try {
      await axios.post(`${API}/contacts`, { ...f, lang });
      toast.success(c.ok);
      setF({ ...empty, service: preset });
    } catch {
      toast.error(c.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-6 md:px-12 py-28 md:py-40 bg-ink-2 border-t border-line">
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHead id="contact" over={c.over} title={c.title} title2={c.title2} sub={c.sub} />
        </div>
        <Reveal className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-10" data-testid="contact-form">
            <Field label={c.name}><input data-testid="contact-name-input" className={fieldCls} value={f.name} onChange={set("name")} /></Field>
            <Field label={c.email}><input data-testid="contact-email-input" type="email" className={fieldCls} value={f.email} onChange={set("email")} /></Field>
            <Field label={c.company}><input data-testid="contact-company-input" className={fieldCls} value={f.company} onChange={set("company")} /></Field>
            <Field label={c.service}><Select testid="contact-service-select" value={f.service} onChange={set("service")} options={serviceOpts} placeholder={c.choose} /></Field>
            <Field label={c.budget}><Select testid="contact-budget-select" value={f.budget} onChange={set("budget")} options={c.budgets} placeholder={c.choose} /></Field>
            <Field label={c.city}><Select testid="contact-city-select" value={f.city} onChange={set("city")} options={cityOpts} placeholder={c.any} /></Field>
            <div className="sm:col-span-2">
              <Field label={c.message}><textarea data-testid="contact-message-input" rows={4} className={`${fieldCls} resize-none`} value={f.message} onChange={set("message")} /></Field>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit-btn"
                className="group inline-flex items-center gap-4 rounded-full bg-[var(--gold)] text-[var(--ink)] pl-8 pr-2 py-2 font-semibold text-lg hover:bg-[var(--gold-2)] disabled:opacity-60 transition-colors duration-300"
              >
                {busy ? c.sending : c.send}
                <span className="w-12 h-12 rounded-full bg-[var(--ink)] text-[var(--gold)] flex items-center justify-center transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight /></span>
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
